import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
    BehaviorSubject,
    filter,
    map,
    Observable,
    Subject,
    take,
    takeUntil,
    tap
} from 'rxjs';
import {
    LgvClientService,
    LgvTSHelper,
    LgvUserService,
    NotificationService
} from '../../core/core.module';
import { LgvModel } from '../../data-models/longevity.model';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '../../../environments/environment';
import { LgvPricingModel } from '../../features/pricing/models/pricing.model';
import { LgvPricingService } from '../../features/pricing/services/pricing.service';
import { LgvAuthMgrService } from '../../core/services/lgv-auth-mgr.service';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { email } from '@rxweb/reactive-form-validators';

@Component({
    selector: 'lgv-lock-request-modal',
    templateUrl: './lock-request-modal.component.html',
    styleUrls: ['./lock-request-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LockRequestModalComponent implements OnInit, OnDestroy {
    isLoading$ = new BehaviorSubject(false);
    opportunityId: LgvModel.Opportunity;
    pricingScenario: LgvPricingModel.PricingScenario;
    loanApplication: LgvLoanApp.LoanApplicationDetail;

    _property: LgvModel.Property = null;
    property$ = new BehaviorSubject(this._property);

    _opportunity: LgvModel.Opportunity = null;
    opportunity$ = new BehaviorSubject(this._opportunity);

    _opportunities: LgvModel.Opportunity[] = null;
    opportunities$ = new BehaviorSubject(this._opportunities);

    pageMode$ = new BehaviorSubject(null);
    showMoreData$ = new BehaviorSubject(false);

    _agentDTO: LgvModel.AgentDTO = null;
    agentDTO$ = new BehaviorSubject(this._agentDTO);

    _company: LgvModel.CompanyInfo = null;
    company$ = new BehaviorSubject(this._company);

    _clientInfo: LgvModel.ClientInfo = null;
    clientInfo$ = new BehaviorSubject(this._clientInfo);

    onDestroy$ = new Subject();

    now = new Date();
    get isProd(): boolean {
        return env.envName === 'PROD' || env.envName === 'UAT';
    }

    //takeUntil(this.onDestroy$), tap(t => { console.log('tap:'); console.log(t); }),
    get isSelectOpportunityMode$(): Observable<boolean> {
        return this.pageMode$.pipe(
            takeUntil(this.onDestroy$),
            map(x => x === 'SelectOpportunity')
        );
    }
    get isHaveSelectedOpportunityMode$(): Observable<boolean> {
        return this.pageMode$.pipe(
            takeUntil(this.onDestroy$),
            map(x => x === 'HaveSelectedOpportunity')
        );
    }
    get filteredOpportunities$(): Observable<LgvModel.Opportunity[]> {
        return this.opportunities$.pipe(
            filter(f => !!f),
            map(arr =>
                arr.filter(
                    o =>
                        o.opportunityInfo?.pricingScenarioId ===
                        this.pricingScenario?.id
                )
            )
        );
    }
    //get lenderFeeWaiverDisplay(): string { return !!this.pricingScenario?.request.borrowerInformation.lenderFeeWaiver ? "Yes" : "No" }
    get impoundsDisplay() {
        // Display is opposite of field
        let waiveEscrows = this.pricingScenario?.request?.borrowerInformation
            ?.waiveEscrows;
        return waiveEscrows == null ? 'Yes' : !!waiveEscrows ? 'No' : 'Yes';
    }

    get compensationTypeForDisplay() {
        //let compType = this.pricingScenario?.request?.borrowerInformation?.compensationType;
        const compType = null;
        return compType ? compType.replace('CompPlan', '') + ' Paid' : 'N/A';
    }

    constructor(
        private http: HttpClient,
        public dialogRef: MatDialogRef<LockRequestModalComponent>,
        public lgvClientSvc: LgvClientService,
        public lgvUserSvc: LgvUserService,
        public lgvNotificationSvc: NotificationService,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public authMgr: LgvAuthMgrService
    ) {
        this.pricingScenario = data.pricingScenario;
        this.loanApplication = data?.loanApplication;

        if (data?.opportunity) {
            this.lgvClientSvc.OpportunitiesState$.next('Ready');
            this.opportunity$.next(data?.opportunity);
            this.pageMode$.next('HaveSelectedOpportunity');
        } else {
            this.pageMode$.next('SelectOpportunity');
        }
    }

    ngOnInit(): void {
        if (this.pricingScenario && this.pricingScenario.propertyId) {
            this.lgvClientSvc.ClientProperties$.pipe(
                filter(f => f !== null),
                take(1)
            ).subscribe(clientProperties => {
                let prop = clientProperties.find(
                    f => f.propertyInfo.id === this.pricingScenario.propertyId
                );
                if (prop) {
                    this.property$.next(prop);
                }
            });
        }

        if (!this.data?.opportunity) {
            this.lgvClientSvc.LoadOpportunitiesForPricingScenario(
                this.pricingScenario,
                true
            );

            this.lgvClientSvc.allOpportunities$
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(x => {
                    this.opportunities$.next(x);
                });
        }

        this.lgvUserSvc.AgentDTO$.pipe(
            filter(f => f !== null),
            take(1)
        ).subscribe(x => {
            this.agentDTO$.next(x);
        });

        this.lgvUserSvc.Company$.pipe(
            filter(f => f !== null),
            take(1)
        ).subscribe(x => {
            this.company$.next(x);
        });

        this.lgvClientSvc.ClientInfo$.pipe(
            filter(f => f !== null),
            take(1)
        ).subscribe(x => {
            this.clientInfo$.next(x);
        });

        this.filteredOpportunities$.pipe(take(1)).subscribe(x => {
            if (x && x.length == 1) {
                this.onSelectThisOpportunity(x[0]);
            }
        });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    onSelectThisOpportunity(o: LgvModel.Opportunity) {
        this.opportunity$.next(o);
        this.pageMode$.next('HaveSelectedOpportunity');
    }

    onSubmit() {
        this.dialogRef.close({ action: true });

        let agentInfo = this.agentDTO$?.value?.agentInfo;
        if (!agentInfo) {
            this.lgvNotificationSvc.error('Missing agent info');
            return;
        }

        let company = this.company$?.value;
        if (!company) {
            this.lgvNotificationSvc.error('Missing company info');
            return;
        }

        let clientInfo = this.clientInfo$?.value;
        if (!clientInfo) {
            this.lgvNotificationSvc.error('Missing client info');
            return;
        }

        let o = this.opportunity$?.value;
        if (!o) {
            this.lgvNotificationSvc.error('Missing opportunity info');
            return;
        }

        let lockRequestModel = {
            emailRequest: {
                templateId: 'F8804E5D-78B6-EE11-B661-6045BD020FA6',
                from: {
                    name: 'HomeDashboard',
                    email: 'homedashboard@realfinity.io'
                },
                tos: [
                    {
                        name: 'Lock Desk',
                        email: this.isProd
                            ? 'lockdesk@realfinity.io'
                            : 'homedashboard+LockDesk@realfinity.io'
                    }
                ],
                ccs: [
                    {
                        name:
                            this.clientInfo$?.value?.owner?.firstName +
                            ' ' +
                            this.clientInfo$?.value?.owner?.lastName,
                        email: this.clientInfo$?.value?.owner?.email
                    }
                ],
                bccs: [
                    {
                        name: 'Home Dashboard Lock Desk',
                        email: this.isProd
                            ? 'homedashboard+LockDesk@realfinity.io'
                            : ''
                    }
                ],
                payload: {
                    agent: {
                        firstName: agentInfo.firstName,
                        lastName: agentInfo.lastName,
                        email: agentInfo.email,
                        title: agentInfo.jobTitle,
                        nmls: agentInfo.nmlsid
                    },
                    company: {
                        name: company.name,
                        nmls: company.nmlsid,
                        address: company.address1,
                        city: company.city,
                        state: company.state,
                        zip: company.zipCode
                    },
                    client: {
                        firstName: clientInfo.firstName,
                        lastName: clientInfo.lastName
                    },
                    pricing: {
                        lockRequestedBy: {
                            firstName: this.authMgr.Auth0UserInfo$?.value
                                ?.given_name,
                            lastName: this.authMgr.Auth0UserInfo$?.value
                                ?.family_name
                        },
                        lenderName: o.opportunityInfo.investor,
                        loanAmount: o.opportunityInfo.loanAmount,
                        rate: o.opportunityInfo.matchedRate,
                        price: o.opportunityInfo.matchedPrice,
                        cost: o.opportunityInfo.closingCost,
                        lockPeriod: o.opportunityInfo.lockPeriod,
                        lockExpiration: this.calcLockExpirationDate(o),
                        costToBorrower: o.opportunityInfo.closingCost,
                        origination: this.compensationTypeForDisplay,
                        impounds: this.impoundsDisplay,
                        productName: o?.opportunityInfo?.productName,

                        //"compPercent": this.pricingScenario?.request?.brokerCriteria?.compPercent,
                        //"includeAdminFeeInRate": !!this.pricingScenario?.request?.borrowerInformation?.lenderFeeWaiver ? 'Yes' : 'No',
                        totalAdjustment: 0, // add below...
                        adjustments: [] // add below...
                    },
                    property: null // add below...
                }
            }
        };

        let totalAdjustments = 0;

        if (o.opportunityInfo?.groupAdjustmentProperties) {
            for (let grpAdj of o.opportunityInfo.groupAdjustmentProperties) {
                for (let adj of grpAdj.adjustments) {
                    totalAdjustments += +adj.adj;

                    lockRequestModel.emailRequest.payload.pricing.adjustments.push(
                        {
                            description: adj.key,
                            adjustment: adj.adj
                        }
                    );
                }
            }
        }

        lockRequestModel.emailRequest.payload.pricing.totalAdjustment = +totalAdjustments.toFixed(
            3
        );

        let propInfo = this.property$.value?.propertyInfo;
        if (!!propInfo) {
            lockRequestModel.emailRequest.payload['property'] = {
                address: propInfo.address,
                city: propInfo.city,
                state: propInfo.state,
                zip: propInfo.zip
            };
        }

        const bldUrl = `${env.LgvApiBaseUrl}/clients/${clientInfo?.id}/pricing/scenarios/${this.pricingScenario?.id}/quotes/${this.opportunity$?.value?.opportunityInfo?.quoteId}/lock-request`;

        return this.http
            .post<any>(bldUrl, lockRequestModel)
            .pipe()
            .subscribe(
                x => {
                    this.lgvClientSvc.loadScenarios();
                    this.lgvClientSvc.loadOpportunities(true);
                    console.log('Response from LockRequest');
                    console.log(x);
                    this.lgvNotificationSvc.success(
                        'Lock Request has been sent'
                    );
                },
                err => {
                    console.log('Error trying to fetch opportunities');
                    console.log(err);
                    this.lgvNotificationSvc.error(
                        'Error trying to fetch opportunities'
                    );
                }
            );
    }

    onCancel() {
        this.dialogRef.close({ action: false });
    }

    //onToggleMoreData() {
    //    let show = this.showMoreData$.value;
    //    this.showMoreData$.next(!show);
    //}

    calcLockExpirationDate(o: LgvModel.Opportunity): string {
        let lockDate = new Date();
        lockDate.setDate(lockDate.getDate() + o.opportunityInfo.lockPeriod);
        return lockDate.toDateString();
    }

    totalAdjustments(): number {
        let opp = this.opportunity$.value;
        if (!opp || !opp?.opportunityInfo?.groupAdjustmentProperties) {
            return 0;
        }

        let result = 0;

        for (let grpAdj of opp.opportunityInfo.groupAdjustmentProperties) {
            for (let adj of grpAdj.adjustments) {
                let adjustment = +adj.adj;
                result += adjustment;
            }
        }

        return result;
    }

    calcEstTotalDue(quote): number {
        let buyerFees;
        if (quote?.lenderFeeDetails) {
            buyerFees = this.buyerFees(quote?.lenderFeeDetails);
        } else {
            buyerFees = this.buyerFees();
        }
        const totalFees = buyerFees?.reduce(
            (sum, fee) => sum + fee.feeTotalCalculatedAmount,
            0
        );
        return totalFees + quote.pointsCost;
    }

    buyerFees(
        lenderFeeDetails?: LgvPricingModel.FeeDetail[]
    ): LgvPricingModel.FeeDetail[] {
        return lenderFeeDetails?.filter(f => f.feePaidBy === 'Buyer');
    }
}
